<template>
  <page-section :title="$t('ProfileMessages_Title')">
    <rest-list :payload="fetchEndpoint">
      <template>
        <rest-list-column-date
          v-if="selectedColumns.create_time"
          prop="create_time"
          :title="$t('ProfileMessages_Field_CreateTime')"
          :width="200"
        />

        <rest-list-column-enum
          v-if="selectedColumns.message_type"
          prop="message_type"
          :title="$t('ProfileMessages_Field_MessageType')"
          :enum="MessageTypeLabel"
          :width="200"
          :sortable="false"
        />

        <rest-list-column
          v-if="selectedColumns.campaign_title"
          prop="campaign_title"
          :min-width="150"
          :title="$t('ProfileMessages_Field_CampaignTitle')"
          :sortable="false"
        >
          <template slot-scope="scope">
            <router-link :to="campaignRoute(scope.row)">
              {{ scope.row.campaign_title }}
            </router-link>
          </template>
        </rest-list-column>

        <rest-list-column-enum
          v-if="selectedColumns.delivery_status"
          prop="delivery_status"
          :title="$t('ProfileMessages_Field_Status')"
          :width="200"
          :enum="MessageDeliveryStatusLabel"
        />

        <rest-list-column-boolean
          v-if="selectedColumns.open"
          prop="open"
          :include-na-label="true"
          :title="$t('ProfileMessages_Field_Open')"
          :width="200"
          :sortable="false"
        />

        <rest-list-column-date
          v-if="selectedColumns.open_time"
          prop="open_time"
          :title="$t('ProfileMessages_Field_OpenTime')"
          :width="200"
          :sortable="false"
        />

        <rest-list-column-boolean
          v-if="selectedColumns.click"
          prop="click"
          :include-na-label="true"
          :title="$t('ProfileMessages_Field_Click')"
          :width="200"
          :sortable="false"
        />

        <rest-list-column-date
          v-if="selectedColumns.click_time"
          prop="click_time"
          :title="$t('ProfileMessages_Field_Click_Date')"
          :width="200"
          :sortable="false"
        />

        <rest-list-column
          v-if="selectedColumns.message_variant"
          prop="message_variant"
          :min-width="150"
          :title="$t('ProfileMessages_Field_MessageVariant')"
          :sortable="false"
          :filterable="false"
          :filter-input="false"
        >
          <template slot-scope="scope">
            {{ variant(scope.row) }}
          </template>
        </rest-list-column>

        <el-table-column :width="50" align="center">
          <template slot="header" slot-scope="{}">
            <rest-list-column-settings
              storage-key="profile-messages-list-columns"
              :title="$t('Profiles_Messages_Field_Settings')"
              :columns="columns"
              v-model="selectedColumns"
            />
          </template>
          <template slot-scope="scope">
            <el-dropdown
              trigger="click"
              class="actions"
              v-if="$can('UserData::ReadAccess')"
            >
              <div class="dropdown-btn-wrapper">
                <i class="el-icon-more"></i>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item>
                    <router-link :to="logRoute(scope.row)">
                      <i class="el-icon-tickets"></i>
                      {{ $t('Profiles_Messages_Details_Action') }}</router-link
                    >
                  </el-dropdown-item>
                </el-dropdown-menu>
              </div>
            </el-dropdown>
          </template>
        </el-table-column>
      </template>
    </rest-list>
  </page-section>
</template>

<script>
import RestList from '@/common/components/rest/RestList'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import RestListColumnBoolean from '@/common/components/rest/RestListColumnBoolean'
import RestListColumnDate from '@/common/components/rest/RestListColumnDate'
import PageSection from '@/common/components/page/PageSection'
import RestListColumnSettings from '@/common/components/rest/RestListColumnSettings'
import { MessageTypeLabel } from '@/common/enum/MessageTypeEnum'
import { MessageDeliveryStatusLabel } from '@/common/enum/MessageDeliveryStatusEnum'
import { projectEndpoint, projectRoute } from '@/core/helpers'

export default {
  name: 'ProfileMessagesPage',

  components: {
    PageSection,
    RestListColumnDate,
    RestListColumnBoolean,
    RestList,
    RestListColumnEnum,
    RestListColumn,
    RestListColumnSettings
  },

  data() {
    return {
      selectedColumns: {},
      MessageTypeLabel,
      MessageDeliveryStatusLabel
    }
  },

  computed: {
    pageTitle() {
      return this.$t(this.$route.meta.title)
    },

    pageSubtitle() {
      return this.$route.query.profile_id
    },

    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `profiles/${this.$route.query.profile_id}/messages`,
        params: { expand: 'campaign_title,delivery_status,ab_variants' }
      })
    },
    readonly() {
      return !this.$can('updateProjectItem') || !this.$can('sendPushCampaign')
    },
    columns() {
      return {
        create_time: {
          title: this.$t('ProfileMessages_Field_CreateTime'),
          defaultValue: true,
          validation: true
        },
        message_type: {
          title: this.$t('ProfileMessages_Field_MessageType'),
          defaultValue: true,
          validation: true
        },
        campaign_title: {
          title: this.$t('ProfileMessages_Field_CampaignTitle'),
          defaultValue: true,
          validation: true
        },
        delivery_status: {
          title: this.$t('ProfileMessages_Field_Status'),
          defaultValue: true,
          validation: true
        },
        open: {
          title: this.$t('ProfileMessages_Field_Open'),
          defaultValue: true,
          validation: true
        },
        open_time: {
          title: this.$t('ProfileMessages_Field_OpenTime'),
          defaultValue: true,
          validation: true
        },
        click: {
          title: this.$t('ProfileMessages_Field_Click'),
          defaultValue: false,
          validation: true
        },
        click_time: {
          title: this.$t('ProfileMessages_Field_Click_Date'),
          defaultValue: false,
          validation: true
        },
        message_variant: {
          title: this.$t('ProfileMessages_Field_MessageVariant'),
          defaultValue: false,
          validation: true
        }
      }
    }
  },
  methods: {
    logRoute(row) {
      return projectRoute({
        name: 'NotificationDetails',
        query: {
          id: row.action_id,
          filters: JSON.stringify({ profile_id: row.profile_id })
        }
      })
    },
    campaignRoute(row) {
      return projectRoute({
        path: `/push/campaign/${this.readonly ? 'view' : 'update'}`,
        query: { id: row.campaign_id }
      })
    },

    variant(row) {
      if (row.message_variant === null) {
        return '-'
      }
      let variant = row.ab_variants[row.message_variant]
      return (
        row.message_variant +
        (variant !== undefined ? ' - ' + variant.title : '')
      )
    }
  }
}
</script>
<style scoped>
.actions .el-icon-more {
  cursor: pointer;
  transform: rotate(90deg);
}
</style>



// WEBPACK FOOTER //
// src/modules/profiles/pages/ProfileMessagesPage.vue